import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  CheckmarkLine,
  ClockLine,
  CopyLine,
  DropboxDashLine,
  LinkLine,
  OpenLine,
  PinLine,
} from '@dropbox/dig-icons/assets';
import {
  aiSearchLinkDestination,
  type AiSearchTableEntityType,
  logAiSearchInteraction,
} from 'analytics/aiSearchAnalytics';
import {makeSavedAiViewId, SavedAiView, savedAiViewsAtom} from 'atoms/savedAiViews';
import {snackbarAtom} from 'atoms/snackbar';
import {
  DashSources,
  dashSources,
  dashSummary,
  progressLines,
  ToolProgressLines,
} from 'components/ai/aiQueryDashStep';
import {DOC_TOOLS, StepDocs, stepDocs} from 'components/ai/aiQueryDocSteps';
import {AiQueryInlineTable} from 'components/ai/AiQueryInlineTable';
import {
  datasetsByHandle,
  expandInlineTables,
  inlineTableHandles,
  splitAnswerSegments,
} from 'components/ai/aiQueryInlineTables';
import {buildStepCsvDownload, StepResponse} from 'components/ai/aiQueryStepDetails';
import {stepIcon} from 'components/ai/aiQueryStepIcons';
import {hasStepRequest, StepRequest} from 'components/ai/aiQueryStepRequest';
import {AiSearchTableAnalyticsContext} from 'components/ai/aiSearchTableAnalytics';
import {parseApprovalMetadata, ToolApprovalInline} from 'components/ai/ToolApprovalInline';
import type {ChatMention} from 'components/DSYS/chat/chatMentions';
import {ChatToolStep} from 'components/DSYS/chat/ChatToolLine';
import {ChatToolLines} from 'components/DSYS/chat/ChatToolLines';
import {ChatUserBubble} from 'components/DSYS/chat/ChatUserBubble';
import {MarkdownView} from 'components/DSYS/MarkdownView';
import {MiniChart} from 'components/DSYS/MiniChart';
import {ROUTE_PATHS} from 'constant';
import {formatShortDate, formatShortDateRange} from 'helpers/dates';
import {GraphableSeries, toGraphableSeries} from 'helpers/graphableTable';
import i18n from 'i18next';
import {useAtom, useSetAtom} from 'jotai';
import {useCallback, useMemo} from 'react';
import {
  AiQueryCapabilityResult,
  AiQueryRecipe,
  AiQueryToolCall,
  type AiQueryToolProgress,
  AiQueryToolResult,
  encodeRecipe,
  MAX_RECIPE_LINK_LENGTH,
  RecipeMode,
} from 'views/admin/adminAiQueryApi';
import {
  AiQueryDatasetSource,
  datasetRowCount,
  downloadDatasetCsv,
  isDownloadableDataset,
} from 'views/admin/aiQueryDatasetDownload';
import {answerCsvDisplayFilename, parseAnswerTables} from 'views/admin/answerTableCsv';

import styles from './aiQueryConversation.module.css';
import {readDocumentTarget, searchDocsHits} from './aiQueryReadDocument';
import STEP_LABELS from './aiQueryStepLabels.json';

export type AiQueryTraceEntry =
  /** `pending` while the model is still writing the call's arguments. */
  | {kind: 'call'; call: AiQueryToolCall; pending?: boolean}
  | {kind: 'result'; result: AiQueryToolResult}
  | {kind: 'progress'; progress: AiQueryToolProgress};

/** A step for a call the model has started writing, before its arguments arrive. */
export const pendingCall = (name: string): AiQueryTraceEntry => ({
  kind: 'call',
  call: {name, args: {}},
  pending: true,
});

/**
 * Records a finished call, filling in the pending step it started as so the step
 * stays in place rather than appearing twice.
 */
export const settlePendingCall = (
  trace: AiQueryTraceEntry[],
  call: AiQueryToolCall
): AiQueryTraceEntry[] => {
  const index = call.parent
    ? -1
    : trace.findIndex(
        (entry) => entry.kind === 'call' && entry.pending && entry.call.name === call.name
      );
  if (index < 0) {
    return [...trace, {kind: 'call', call}];
  }
  return trace.map((entry, i) => (i === index ? {kind: 'call', call} : entry));
};

export type AiQueryAssistantState = {
  answer: string;
  trace: AiQueryTraceEntry[];
  results: AiQueryCapabilityResult[];
  /** Streamed dataset rows by handle, for the answer's inline table markers. */
  datasets?: Record<string, AiQueryCapabilityResult>;
  recipe: AiQueryRecipe | null;
  /**
   * The server's id for this turn's datasets: downloads fetch a table's full
   * rows with it (the stream only carries previews). Ask drawer only.
   */
  datasetTurnId?: string;
  /** The `elevated` the turn streamed with, so a replayed download matches. */
  elevated?: boolean;
  thoughtSeconds: number | null;
  error: string | null;
  isStreaming: boolean;
};

export type AiQueryTurn = {
  id: string;
  question: string;
  /** People @mentioned in `question`. */
  mentions?: ChatMention[];
  assistant: AiQueryAssistantState;
};

export const AI_SEARCH_SUGGESTIONS = [
  'Who owns the most workstreams?',
  'Which workstream metrics are most common?',
  'Show my org’s enneagrams',
  'Which peers have the longest tenure?',
  'Summarize the latest 3 news posts',
] as const;

export const AI_QUERY_EXAMPLE_QUESTIONS = [
  'Find people in Germany who own a workstream that is at risk and list the workstream owner.',
  'What badges have managers under the OCEO team earned?',
  'Who on Core is on PTO this week, and do any of them own a workstream that is off track? Include their latest update.',
  'List everyone hired in the last 90 days who is already a DRI on an at-risk workstream, and summarize what each of those workstreams last reported.',
  'For the workstreams under Engineering that reported off track last week, who are the DRIs and what did they say & are any of those DRIs out of office this week?',
  'Which managers have a direct report who is on PTO this week and is also a DRI on an off-track workstream?',
  'Cross-reference recent gratitude with workstream health: who received gratitude lately and also owns an on-track workstream?',
  "Summarize this week's company news for managers, then list any off-track workstreams whose latest update mentions a related theme.",
] as const;

/**
 * The tool a step's labels are keyed by: the unified `count` tool reads as the
 * type it counts (`count_people`), as its dataset does, once its arguments arrive.
 */
const stepLabelTool = (call: AiQueryToolCall): string =>
  call.name === 'count' && typeof call.args?.type === 'string'
    ? `count_${call.args.type}`
    : call.name;

/**
 * What a step says while it runs: `ai_query_activity_<tool>`. Every tool the
 * agents offer has one (`test_ai_query_step_labels.py`); a tool from a newer
 * server falls back to its name.
 */
const runningLabel = (tool: string): string => {
  const key = `ai_query_activity_${tool}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  const words = tool.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const flattenNestedBlockquotes = (markdown: string): string =>
  markdown.replace(/^(> *)> /gm, '$1').replace(/^>\s*$/gm, '');

export const ensureBlockSpacing = (markdown: string): string => {
  const lines = markdown.split('\n');
  const out: string[] = [];
  let inFence = false;

  const isTableRowLine = (line: string): boolean => line.includes('|');

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
      inFence = !inFence;
      out.push(line);
      continue;
    }

    out.push(line);

    if (inFence) {
      continue;
    }

    const next = lines[i + 1];
    if (next === undefined) {
      continue;
    }
    if (trimmed === '' || next.trim() === '') {
      continue;
    }
    if (isTableRowLine(line) && isTableRowLine(next)) {
      continue;
    }
    out.push('');
  }

  return out.join('\n');
};

const reformatIsoDatesInLine = (line: string): string => {
  // Split on markdown link URLs to avoid corrupting dates inside them.
  // Segments at odd indices are the `[text](url)` matches — leave untouched.
  const parts = line.split(/(\[[^\]]*\]\([^)]*\))/g);
  const range = /(\d{4}-\d{2}-\d{2})\s*(?:→|->|–|—|to)\s*(\d{4}-\d{2}-\d{2})/g;
  const single = /\d{4}-\d{2}-\d{2}(?![T\d:])/g;
  return parts
    .map((part, i) =>
      i % 2 === 1
        ? part
        : part
            .replace(range, (_m, start, end) => formatShortDateRange(start, end))
            .replace(single, (m) => formatShortDate(m))
    )
    .join('');
};

export const formatMarkdownDates = (markdown: string): string => {
  let inFence = false;
  return markdown
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
        inFence = !inFence;
        return line;
      }
      return inFence ? line : reformatIsoDatesInLine(line);
    })
    .join('\n');
};

/** The agent's own bookkeeping (combining, aggregating), shown as one line. */
const TRANSFORM_TOOLS = new Set(STEP_LABELS.bookkeeping);

/**
 * Tools that do one thing (draft an update, read a document) rather than look
 * things up: once done, their line says nothing more (the shimmer stopping says
 * it), unless it didn't work.
 */
const OUTCOME_TOOLS = new Set(STEP_LABELS.outcome);

/**
 * Tools whose arguments the model writes for a while (a drafted update), so
 * their line says what they're doing before the arguments arrive.
 */
const LONG_ARGUMENT_TOOLS = new Set([
  'propose_workstream_update',
  'propose_workstream_metric_update',
]);

/**
 * A `count_*` call whose buckets are grouped (group_by != 'none'), where
 * `total_found` is the grand total across ALL buckets — not the rows shown. A
 * single bucket with a null `group` is the ungrouped total, so it's excluded.
 */
const isGroupedCount = (
  toolName: string,
  items: Array<Record<string, unknown>> | undefined
): boolean => {
  if (!toolName.startsWith('count_') || !items || items.length === 0) {
    return false;
  }
  return items.length > 1 || items[0]?.group != null;
};

const GROUP_NOUN_SINGULAR: Record<string, string> = {
  person: 'person',
};

const GROUP_NOUN_PLURAL: Record<string, string> = {
  person: 'people',
};

/**
 * Turn a `group_by` dimension into a readable, count-aware noun for the grouped
 * count summary, e.g. `team` -> "teams", `person` -> "people", `country` ->
 * "countries". Handles common irregulars/endings with a naive `+s` fallback.
 */
const groupNoun = (groupBy: string, count: number): string => {
  const base = groupBy.replace(/_/g, ' ').trim().toLowerCase();
  if (!base) {
    return '';
  }
  if (count === 1) {
    return GROUP_NOUN_SINGULAR[base] ?? base;
  }
  if (GROUP_NOUN_PLURAL[base]) {
    return GROUP_NOUN_PLURAL[base];
  }
  if (/(s|ss|sh|ch|x|z)$/.test(base)) {
    return `${base}es`;
  }
  if (/[^aeiou]y$/.test(base)) {
    return `${base.slice(0, -1)}ies`;
  }
  return `${base}s`;
};

/**
 * What a finished step found, after its label: "5 found", "12 total", "3 metric
 * types, 1,089 total". A search that found nothing says nothing.
 */
const resultSummary = (
  toolName: string,
  total: number,
  truncated: boolean,
  items?: Array<Record<string, unknown>>,
  groupBy?: unknown
): string | undefined => {
  const totalLabel = total.toLocaleString();
  // Grouped counts: distinguish "top N buckets of a larger set, X total" from
  // "X found", which misreads as the rows shown.
  if (isGroupedCount(toolName, items)) {
    const shown = items?.length ?? 0;
    // Name the group dimension ("teams") when we know it; the bare "N groups"
    // fallback covers calls where group_by wasn't echoed in the args.
    const noun = typeof groupBy === 'string' ? groupNoun(groupBy, shown) : '';
    if (noun) {
      return truncated
        ? i18n.t('ai_query_step_top_groups_named', {n: shown, group: noun, total: totalLabel})
        : i18n.t('ai_query_step_groups_named', {n: shown, group: noun, total: totalLabel});
    }
    return truncated
      ? i18n.t('ai_query_step_top_groups', {n: shown, total: totalLabel})
      : i18n.t('ai_query_step_groups', {count: shown, n: shown, total: totalLabel});
  }
  if (toolName.startsWith('count')) {
    return i18n.t('ai_query_step_total', {n: totalLabel});
  }
  return total > 0 ? i18n.t('ai_query_step_found', {count: total, n: totalLabel}) : undefined;
};

const MAX_QUERY_LABEL_LENGTH = 40;

/** An opened step shows its first few rows; its CSV download has them all. */
const STEP_PREVIEW_ROWS = 5;

/** The arguments a step's label names, so its details needn't repeat them. */
const QUERY_ARGS = ['query', 'name_query'];

/** What a search step looked for, short enough for its one-line label. */
const searchQuery = (args: Record<string, unknown> | undefined): string | undefined => {
  const raw = QUERY_ARGS.map((key) => args?.[key]).find((value) => typeof value === 'string');
  const query = typeof raw === 'string' ? raw.trim() : '';
  if (!query) {
    return undefined;
  }
  return query.length > MAX_QUERY_LABEL_LENGTH
    ? `${query.slice(0, MAX_QUERY_LABEL_LENGTH - 1).trimEnd()}…`
    : query;
};

/** "Searched Knowledge articles for “401k”" */
const withQuery = (label: string, query: string | undefined): string =>
  query ? i18n.t('ai_query_step_for_query', {label, query}) : label;

/** Download a step's debug row sample (Studio, older payloads: no dataset). */
const stepSampleDownload = (
  toolName: string,
  items: Array<Record<string, unknown>>,
  stepAnalytics?: {onStepCsvDownload: (toolName: string, rowCount: number) => void}
): (() => void) | undefined => {
  const download = items.length > 0 ? buildStepCsvDownload(toolName, items) : undefined;
  return download
    ? () => {
        stepAnalytics?.onStepCsvDownload(toolName, items.length);
        download();
      }
    : undefined;
};

/**
 * Each result's call, by trace index. Results arrive in completion order, and a
 * sub-agent's before its parent's, so a result pairs with the call carrying its
 * id, or (prefetched calls and older servers send none) the earliest open call
 * with its name under the same parent.
 */
const pairResults = (trace: AiQueryTraceEntry[]): Map<number, AiQueryToolResult> => {
  const paired = new Map<number, AiQueryToolResult>();
  trace.forEach((entry, resultIndex) => {
    if (entry.kind !== 'result') {
      return;
    }
    const {result} = entry;
    const callIndex = trace.findIndex(
      (candidate, i) =>
        i < resultIndex &&
        candidate.kind === 'call' &&
        !paired.has(i) &&
        (candidate.call.parent ?? null) === (result.parent ?? null) &&
        (candidate.call.id && result.id
          ? candidate.call.id === result.id
          : candidate.call.name === result.name)
    );
    if (callIndex >= 0) {
      paired.set(callIndex, result);
    }
  });
  return paired;
};

/**
 * Each call's progress, by trace index: a progress entry belongs to the latest
 * top-level call of the tool it names as `parent` (so a parallel call between
 * them doesn't take it), or without one to the call just before it.
 */
const progressByCall = (trace: AiQueryTraceEntry[]): Map<number, AiQueryToolProgress[]> => {
  const byCall = new Map<number, AiQueryToolProgress[]>();
  const latestCall = new Map<string, number>();
  let lastCall = -1;
  trace.forEach((entry, i) => {
    if (entry.kind === 'call') {
      lastCall = i;
      if (!entry.call.parent) {
        latestCall.set(entry.call.name, i);
      }
      return;
    }
    if (entry.kind !== 'progress') {
      return;
    }
    const {progress} = entry;
    // Dash's generic "Thinking" says nothing the activity line doesn't already.
    if (/^thinking\b/i.test(progress.message) && !progress.searches?.length) {
      return;
    }
    const owner = progress.parent ? (latestCall.get(progress.parent) ?? -1) : lastCall;
    if (owner >= 0) {
      byCall.set(owner, [...(byCall.get(owner) ?? []), progress]);
    }
  });
  return byCall;
};

export const traceToSteps = (
  trace: AiQueryTraceEntry[],
  stepAnalytics?: {
    onTableLinkClick: (props: {
      entityType: AiSearchTableEntityType;
      columnKind: string;
      path: string;
      toolName: string;
    }) => void;
    onStepCsvDownload: (toolName: string, rowCount: number) => void;
    onStepExpand: (toolName: string, expanded: boolean) => void;
  },
  /**
   * The turn's datasets by handle: a step with one shows its preview and
   * downloads all its rows (from `source`). Without them a step falls back to
   * the debug row sample its result carried.
   */
  steps?: {
    datasets: Record<string, AiQueryCapabilityResult>;
    source?: AiQueryDatasetSource;
    onDownloadError: (error: unknown) => void;
    /** Handles of the datasets the answer shows as tables. */
    inAnswer?: ReadonlySet<string>;
  }
): ChatToolStep[] => {
  const results = pairResults(trace);
  const progress = progressByCall(trace);
  const searchHits = searchDocsHits(trace, steps?.datasets);
  return trace.flatMap((entry, entryIndex) => {
    if (entry.kind !== 'call') {
      return [];
    }
    const result = results.get(entryIndex);
    const progressEntries = progress.get(entryIndex) ?? [];

    const toolName = entry.call.name;
    const labelTool = stepLabelTool(entry.call);
    const isTransform = TRANSFORM_TOOLS.has(toolName);
    // Dash answers in prose, so its "rows" just echo the answer; its searches
    // and the sources it cited are the detail.
    const isDash = toolName === 'ask_dash';
    const stepDataset = result?.handle ? steps?.datasets[result.handle] : undefined;
    const citedSources = isDash ? dashSources(stepDataset?.items ?? result?.items) : [];
    const dataset = isDash ? undefined : stepDataset;
    const items = isDash ? [] : (dataset?.items ?? result?.items ?? []);
    const dashLines = isDash ? progressLines(progressEntries) : [];
    const query = searchQuery(entry.call.args);
    const isOutcome = OUTCOME_TOOLS.has(toolName);
    // A document a search found is named from the start ("Reading “AI Tools”");
    // one opened from a link gets its title once it's read.
    const doc =
      toolName === 'read_document' ? readDocumentTarget(entry.call, items, searchHits) : undefined;
    const docs = DOC_TOOLS.has(toolName) ? stepDocs(items) : undefined;
    // One text from when it's known to when it's done, so nothing on the line is
    // replaced: until its arguments arrive (a moment) the line is just its icon,
    // then it says what it does, and when it's done what it found follows.
    const label = isDash
      ? runningLabel(toolName)
      : entry.pending && !LONG_ARGUMENT_TOOLS.has(toolName) && !isTransform
        ? ''
        : doc?.fromSearch
          ? i18n.t('ai_query_step_reading_document', {title: doc.title})
          : withQuery(runningLabel(labelTool), query);
    const found =
      !result || isTransform || doc?.fromSearch
        ? undefined
        : isDash
          ? dashSummary(progressEntries)
          : isOutcome
            ? (doc?.title ??
              (result.total_found === 0 ? i18n.t('ai_query_step_failed') : undefined))
            : resultSummary(
                labelTool,
                result.total_found ?? 0,
                !!result.truncated,
                result.items,
                entry.call.args?.group_by
              );
    // The label already says what it counted and searched for.
    const requestSkip = [...QUERY_ARGS, ...(toolName === 'count' ? ['type'] : [])];
    // An opened step shows its rows only when the answer doesn't already.
    const shownInAnswer = Boolean(result?.handle && steps?.inAnswer?.has(result.handle));
    const response =
      citedSources.length > 0 ? (
        <DashSources sources={citedSources} />
      ) : docs ? (
        docs.length > 0 ? (
          <StepDocs docs={docs} />
        ) : undefined
      ) : items.length > 0 && !shownInAnswer ? (
        <AiSearchTableAnalyticsContext.Provider
          value={{
            toolName,
            onLinkClick: stepAnalytics
              ? (props) => stepAnalytics.onTableLinkClick({...props, toolName})
              : undefined,
          }}
        >
          <StepResponse
            toolName={dataset?.capability ?? toolName}
            items={items}
            totalFound={result?.total_found ?? null}
            truncated={!!result?.truncated}
            previewRows={STEP_PREVIEW_ROWS}
          />
        </AiSearchTableAnalyticsContext.Provider>
      ) : undefined;
    const detailLines = isDash ? dashLines : progressLines(progressEntries);
    const progressDetail =
      detailLines.length > 0 ? <ToolProgressLines lines={detailLines} /> : undefined;

    // A list of links isn't a dataset worth a CSV.
    const onDownload = docs
      ? undefined
      : dataset && isDownloadableDataset(dataset)
        ? async () => {
            stepAnalytics?.onStepCsvDownload(toolName, datasetRowCount(dataset));
            try {
              await downloadDatasetCsv(dataset, steps?.source);
            } catch (error) {
              steps?.onDownloadError(error);
            }
          }
        : stepSampleDownload(toolName, items, stepAnalytics);
    return [
      {
        label,
        result: found,
        toolName,
        icon: stepIcon(toolName, isTransform, doc?.source),
        detail: progressDetail,
        // A read's label names its document; its doc id and offset say nothing more.
        request:
          !isTransform &&
          toolName !== 'read_document' &&
          hasStepRequest(entry.call.args, requestSkip) ? (
            <StepRequest args={entry.call.args} skip={requestSkip} />
          ) : undefined,
        response,
        onDownload,
        onResponseToggle: stepAnalytics
          ? (expanded) => stepAnalytics.onStepExpand(toolName, expanded)
          : undefined,
        isPending: !result,
        isBookkeeping: isTransform,
      },
    ];
  });
};

export const emptyAssistant = (isStreaming: boolean): AiQueryAssistantState => ({
  answer: '',
  trace: [],
  results: [],
  datasets: {},
  recipe: null,
  thoughtSeconds: null,
  error: null,
  isStreaming,
});

export const newTurnId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const MAX_TITLE_LENGTH = 120;

const buildRecipeViewUrl = (encoded: string, mode: RecipeMode, title?: string): string => {
  const base = `${window.location.origin}${ROUTE_PATHS.ADMIN_AI_VIEW}?recipe=${encoded}&mode=${mode}`;
  const trimmed = title?.trim().slice(0, MAX_TITLE_LENGTH).trim();
  return trimmed ? `${base}&title=${encodeURIComponent(trimmed)}` : base;
};

const SaveLiveViewMenu = ({
  recipe,
  title,
  onCopy,
}: {
  recipe: AiQueryRecipe;
  title?: string;
  onCopy: (text: string, message: string) => void;
}) => {
  const [savedViews, setSavedViews] = useAtom(savedAiViewsAtom);
  const setSnackbar = useSetAtom(snackbarAtom);

  const encoded = useMemo(() => encodeRecipe(recipe), [recipe]);
  const linkFits = encoded.length <= MAX_RECIPE_LINK_LENGTH;
  const homeId = useMemo(() => makeSavedAiViewId(encoded, 'live'), [encoded]);
  const isSavedToHome = savedViews.some((v) => v.id === homeId);
  const homeTitle = title?.trim() || 'Saved view';

  const open = (mode: RecipeMode) =>
    window.open(buildRecipeViewUrl(encoded, mode, title), '_blank', 'noopener,noreferrer');

  const toggleHome = () => {
    if (isSavedToHome) {
      setSavedViews((prev) => prev.filter((v) => v.id !== homeId));
      setSnackbar({text: 'Removed from home'});
      return;
    }
    const next: SavedAiView = {
      id: homeId,
      title: homeTitle,
      recipe: encoded,
      mode: 'live',
      savedAt: Date.now(),
    };
    setSavedViews((prev) => [next, ...prev.filter((v) => v.id !== homeId)]);
    setSnackbar({text: 'Saved to home'});
  };

  return (
    <Menu.Wrapper>
      {({getTriggerProps, getContentProps}) => (
        <>
          <Chip variant="standard" size="small" withDropdownIcon {...getTriggerProps()}>
            <Chip.IconAccessory>
              <UIIcon src={OpenLine} size="small" />
            </Chip.IconAccessory>
            <Chip.Content>Save live view</Chip.Content>
          </Chip>
          <Menu.Content {...getContentProps()} placement="top-start" minWidth="240px">
            <Menu.Segment>
              {linkFits ? (
                <Menu.ActionItem
                  withLeftAccessory={<UIIcon src={LinkLine} />}
                  onClick={() => onCopy(buildRecipeViewUrl(encoded, 'live', title), 'Link copied')}
                >
                  Copy sharable link
                </Menu.ActionItem>
              ) : (
                <Menu.ActionItem
                  withLeftAccessory={<UIIcon src={CopyLine} />}
                  onClick={() => onCopy(JSON.stringify(recipe), 'Recipe copied')}
                >
                  Copy recipe
                </Menu.ActionItem>
              )}
              <Menu.ActionItem
                withLeftAccessory={<UIIcon src={OpenLine} />}
                onClick={() => open('live')}
              >
                Open saved live view
              </Menu.ActionItem>
              <Menu.ActionItem
                withLeftAccessory={<UIIcon src={ClockLine} />}
                onClick={() => open('snapshot')}
              >
                Open saved snapshot
              </Menu.ActionItem>
            </Menu.Segment>
            <Menu.Segment>
              <Menu.ActionItem
                withLeftAccessory={<UIIcon src={isSavedToHome ? CheckmarkLine : PinLine} />}
                onClick={toggleHome}
              >
                {isSavedToHome ? 'Remove from home dashboard' : 'Add view to home dashboard'}
              </Menu.ActionItem>
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

type AiQueryGraphOffer = {key: string; label: string; graph: GraphableSeries};

/** Chartable tables embedded in the agent answer markdown (opt-in mini charts). */
const resolveGraphOffers = (answer: string): AiQueryGraphOffer[] => {
  const tables = parseAnswerTables(answer);
  return tables.flatMap((table, index) => {
    const graph = toGraphableSeries(table);
    if (!graph) {
      return [];
    }
    const meta = {startIdx: table.startIdx, index, total: tables.length};
    const label = answerCsvDisplayFilename(answer, meta).replace(/\.csv$/i, '');
    return [{key: `graph-${index}`, label, graph}];
  });
};

export const AiQueryConversationTurn = ({
  turn,
  onCopy,
  answerContentClassName,
  frameClassName,
  showSaveLiveView = false,
  enableAnalytics = false,
}: {
  turn: AiQueryTurn;
  onCopy: (text: string, message?: string) => void;
  answerContentClassName?: string;
  frameClassName?: string;
  showSaveLiveView?: boolean;
  enableAnalytics?: boolean;
}) => {
  const {question, assistant} = turn;
  const stepAnalytics = useMemo(
    () =>
      enableAnalytics
        ? {
            onTableLinkClick: (props: {
              entityType: AiSearchTableEntityType;
              columnKind: string;
              path: string;
              toolName: string;
            }) =>
              logAiSearchInteraction({
                action: 'table_link_click',
                entityType: props.entityType,
                destination: aiSearchLinkDestination(props.path),
                toolName: props.toolName,
                columnKind: props.columnKind,
              }),
            onStepCsvDownload: (toolName: string, rowCount: number) =>
              logAiSearchInteraction({
                action: 'csv_download',
                scope: 'step',
                toolName,
                rowCount,
              }),
            onStepExpand: (toolName: string, expanded: boolean) =>
              logAiSearchInteraction({
                action: 'step_expand',
                toolName,
                expanded,
              }),
          }
        : undefined,
    [enableAnalytics]
  );
  const setSnackbar = useSetAtom(snackbarAtom);
  const datasets = useMemo(
    () => datasetsByHandle(assistant.datasets ?? {}, assistant.results),
    [assistant.datasets, assistant.results]
  );
  const datasetSource = useMemo<AiQueryDatasetSource | undefined>(
    () =>
      assistant.datasetTurnId
        ? {
            turnId: assistant.datasetTurnId,
            recipe: assistant.recipe,
            elevated: assistant.elevated,
          }
        : undefined,
    [assistant.datasetTurnId, assistant.recipe, assistant.elevated]
  );
  const onDownloadError = useCallback(
    (error: unknown) =>
      setSnackbar({
        text: error instanceof Error && error.message ? error.message : 'Download failed',
      }),
    [setSnackbar]
  );
  const onTableDownload = useMemo(
    () =>
      enableAnalytics
        ? (rowCount: number) =>
            logAiSearchInteraction({action: 'csv_download', scope: 'answer', rowCount})
        : undefined,
    [enableAnalytics]
  );
  // Tables the answer shows, so their steps don't show them again.
  const answerTableKey = inlineTableHandles(assistant.answer).join(' ');
  const answerTables = useMemo(
    () => new Set(answerTableKey.split(' ').filter(Boolean)),
    [answerTableKey]
  );
  const agentSteps = useMemo(
    () =>
      traceToSteps(assistant.trace, stepAnalytics, {
        datasets,
        source: datasetSource,
        onDownloadError,
        inAnswer: answerTables,
      }),
    [assistant.trace, stepAnalytics, datasets, datasetSource, onDownloadError, answerTables]
  );
  const renderedAnswer = useMemo(
    () => ensureBlockSpacing(formatMarkdownDates(flattenNestedBlockquotes(assistant.answer))),
    [assistant.answer]
  );
  const answerSegments = useMemo(
    () => splitAnswerSegments(renderedAnswer, assistant.isStreaming),
    [renderedAnswer, assistant.isStreaming]
  );
  // The answer as plain Markdown, with inline tables written out, for chart
  // offers (which read Markdown tables).
  const plainAnswer = useMemo(
    () =>
      assistant.isStreaming || inlineTableHandles(assistant.answer).length === 0
        ? assistant.answer
        : expandInlineTables(assistant.answer, datasets),
    [assistant.answer, assistant.isStreaming, datasets]
  );
  const graphOffers = useMemo(
    () => (assistant.isStreaming ? [] : resolveGraphOffers(plainAnswer)),
    [plainAnswer, assistant.isStreaming]
  );
  const usedDash = useMemo(() => {
    if (assistant.isStreaming) return false;
    const calls = assistant.trace.filter((e) => e.kind === 'call' && e.call.name === 'ask_dash');
    if (calls.length === 0) return false;
    const results = assistant.trace.filter(
      (e) => e.kind === 'result' && e.result.name === 'ask_dash' && (e.result.total_found ?? 0) > 0
    );
    return results.length > 0;
  }, [assistant.trace, assistant.isStreaming]);
  const recipeReady =
    !assistant.isStreaming && assistant.recipe != null && assistant.recipe.steps.length > 0;
  const approvalItems = useMemo(
    () =>
      assistant.isStreaming
        ? []
        : assistant.results.flatMap((r) =>
            r.items
              .map((item) => {
                const approval = parseApprovalMetadata(item);
                return approval ? {approval, item} : null;
              })
              .filter((v): v is NonNullable<typeof v> => v != null)
          ),
    [assistant.results, assistant.isStreaming]
  );
  const toolLines = (
    <Box className={frameClassName}>
      <ChatToolLines
        steps={agentSteps}
        isStreaming={assistant.isStreaming}
        working={assistant.isStreaming && assistant.answer.length === 0}
      />
    </Box>
  );
  const showLiveView = showSaveLiveView && recipeReady && assistant.recipe != null;
  const answerActions =
    assistant.isStreaming || !assistant.answer || (!usedDash && !showLiveView) ? null : (
      <Box className={frameClassName}>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          {usedDash && (
            <Tooltip title={i18n.t('ai_query_via_dash_tooltip')} placement="bottom" openDelay={300}>
              <Box>
                <Chip variant="attention" size="small" style={{pointerEvents: 'none'}}>
                  <Chip.IconAccessory>
                    <UIIcon src={DropboxDashLine} size="small" />
                  </Chip.IconAccessory>
                  <Chip.Content>{i18n.t('ai_query_via_dash')}</Chip.Content>
                </Chip>
              </Box>
            </Tooltip>
          )}
          {showLiveView && assistant.recipe != null && (
            <SaveLiveViewMenu recipe={assistant.recipe} title={question} onCopy={onCopy} />
          )}
        </Box>
      </Box>
    );

  const answerBlock = assistant.answer ? (
    <Box className={styles.answerArea}>
      {answerSegments.map((segment, i) =>
        segment.kind === 'table' ? (
          <AiQueryInlineTable
            key={`segment-${i}`}
            dataset={datasets[segment.handle]}
            isStreaming={assistant.isStreaming}
            source={datasetSource}
            onDownload={onTableDownload}
            onDownloadError={onDownloadError}
          />
        ) : (
          <MarkdownView
            key={`segment-${i}`}
            value={segment.markdown}
            contentClassName={answerContentClassName}
            // Only the last run is still growing; earlier ones are final.
            isStreaming={assistant.isStreaming && i === answerSegments.length - 1}
          />
        )
      )}
      {graphOffers.map((offer) => (
        <Box key={offer.key} className={`${styles.chartCap} ${atoms({paddingTop: '12'})}`}>
          {graphOffers.length > 1 && (
            <Text size="small" color="subtle">
              {offer.label}
            </Text>
          )}
          <MiniChart graph={offer.graph} />
        </Box>
      ))}
    </Box>
  ) : null;

  return (
    <Stack gap="12">
      {question && (
        <Box className={frameClassName}>
          <ChatUserBubble mentions={turn.mentions}>{question}</ChatUserBubble>
        </Box>
      )}

      {assistant.error && (
        <Box
          padding="12"
          borderRadius="Medium"
          className={`${atoms({backgroundColor: 'Background Subtle'})}${
            frameClassName ? ` ${frameClassName}` : ''
          }`}
        >
          <Text color="error">{assistant.error}</Text>
        </Box>
      )}

      {(agentSteps.length > 0 || assistant.isStreaming) && toolLines}

      {answerBlock}

      {answerActions}

      {approvalItems.map(({approval, item}, i) => (
        <Box key={`ai-approval-${i}`} className={frameClassName}>
          <ToolApprovalInline approval={approval} item={item} elevated={assistant.elevated} />
        </Box>
      ))}
    </Stack>
  );
};
